import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  TextField,
  Autocomplete,
  FormControlLabel,
  Switch,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  InputAdornment,
} from "@mui/material";
import { HiPlus, HiRefresh, HiSearch } from "react-icons/hi";
import { useSnackbar } from "notistack";
import {
  useAdminCreateUser,
  useAdminIdTypes,
  useAdminAllOrganizations,
  useAdminRolesByOrg,
  useAdminCreateAssignment,
} from "@/api/hooks/admin.hooks";
import type { UserDto, IdTypeDto, OrganizationDto } from "@/api/types/dtos";
import type { RoleDto } from "@/api/types/dtos/role-privilege.dto";

const STEPS = ["Información del Usuario", "Asignar a Organización"];

function generateUUID(): string {
  return crypto.randomUUID();
}

interface CreateUserModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({ open, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [activeStep, setActiveStep] = useState(0);
  const [createdUserId, setCreatedUserId] = useState<string | null>(null);
  const [submittingUser, setSubmittingUser] = useState(false);
  const [submittingAssignment, setSubmittingAssignment] = useState(false);

  const [uuid, setUuid] = useState(generateUUID);
  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [nickName, setNickName] = useState("");
  const [idType, setIdType] = useState<IdTypeDto | null>(null);
  const [idNumber, setIdNumber] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [gender, setGender] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [selectedOrg, setSelectedOrg] = useState<OrganizationDto | null>(null);
  const [selectedRole, setSelectedRole] = useState<RoleDto | null>(null);
  const [skipAssignment, setSkipAssignment] = useState(false);

  const { data: idTypesData } = useAdminIdTypes();
  const idTypes: IdTypeDto[] = (idTypesData?.data ?? []) as IdTypeDto[];

  const { data: orgsData } = useAdminAllOrganizations();
  const organizations: OrganizationDto[] = (orgsData?.data ?? []) as OrganizationDto[];

  const { data: rolesData, isLoading: rolesLoading } = useAdminRolesByOrg(selectedOrg?.id || "");
  const roles: RoleDto[] = (rolesData?.data ?? []) as RoleDto[];

  const createUserMutation = useAdminCreateUser();
  const createAssignmentMutation = useAdminCreateAssignment();

  const handleReset = useCallback(() => {
    setActiveStep(0);
    setCreatedUserId(null);
    setSubmittingUser(false);
    setSubmittingAssignment(false);
    setUuid(generateUUID());
    setName("");
    setLastName("");
    setMiddleName("");
    setNickName("");
    setIdType(null);
    setIdNumber("");
    setEmail("");
    setAddress("");
    setBirthdate("");
    setGender("");
    setIsActive(true);
    setErrors({});
    setSelectedOrg(null);
    setSelectedRole(null);
    setSkipAssignment(false);
  }, []);

  useEffect(() => {
    if (open) handleReset();
  }, [open, handleReset]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!uuid.trim()) newErrors.uuid = "El UUID es obligatorio";
    if (
      uuid.trim() &&
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(uuid.trim())
    ) {
      newErrors.uuid = "Formato de UUID inválido";
    }
    if (!name.trim()) newErrors.name = "El nombre es obligatorio";
    if (!idType) newErrors.idType = "El tipo de documento es obligatorio";
    if (!idNumber.trim()) newErrors.idNumber = "El número de documento es obligatorio";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateUser = async () => {
    if (!validate()) return;

    setSubmittingUser(true);
    try {
      const userDto: UserDto = {
        id: uuid.trim(),
        name: name.trim(),
        lastName: lastName.trim() || undefined,
        middleName: middleName.trim() || undefined,
        nickName: nickName.trim() || undefined,
        idTypeId: idType!.id,
        idNumber: idNumber.trim(),
        email: email.trim() || undefined,
        address: address.trim() || undefined,
        birthdate: birthdate || undefined,
        gender: gender || undefined,
        isActive,
      };

      const result = await createUserMutation.mutateAsync(userDto);
      const newUserId = (result?.data as UserDto)?.id || uuid.trim();
      setCreatedUserId(newUserId);
      enqueueSnackbar("Usuario creado exitosamente", { variant: "success" });
      setActiveStep(1);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { description?: string; message?: string } } };
      const message =
        err?.response?.data?.description ||
        err?.response?.data?.message ||
        "Error al crear el usuario";
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setSubmittingUser(false);
    }
  };

  const handleAssignAndFinish = async () => {
    if (!createdUserId) return;

    if (skipAssignment || !selectedOrg || !selectedRole) {
      onSuccess();
      onClose();
      return;
    }

    setSubmittingAssignment(true);
    try {
      await createAssignmentMutation.mutateAsync({
        userId: createdUserId,
        roleId: selectedRole.id,
        organizationId: selectedOrg.id,
      });
      enqueueSnackbar(`Usuario asignado a "${selectedOrg.name}" como "${selectedRole.name}"`, {
        variant: "success",
      });
      onSuccess();
      onClose();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { description?: string; message?: string } } };
      const message =
        err?.response?.data?.description ||
        err?.response?.data?.message ||
        "Error al asignar el usuario a la organización";
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setSubmittingAssignment(false);
    }
  };

  const handleClose = () => {
    if (submittingUser || submittingAssignment) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="create-user-dialog-title"
      PaperProps={{
        sx: { borderTop: "4px solid", borderColor: "primary.main" },
      }}
    >
      <DialogTitle id="create-user-dialog-title" sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <HiPlus size={22} />
          <Box>
            <Typography variant="h6" component="span" fontWeight={600}>
              Nuevo Usuario
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Completa los campos para registrar un nuevo usuario en el sistema
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                Identificador
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <TextField
                  label="UUID"
                  required
                  fullWidth
                  value={uuid}
                  onChange={(e) => setUuid(e.target.value)}
                  error={!!errors.uuid}
                  helperText={errors.uuid || "Identificador único del usuario"}
                  disabled={submittingUser}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  id="create-user-uuid"
                  inputProps={{
                    "aria-invalid": !!errors.uuid,
                    "aria-describedby": errors.uuid ? "create-user-uuid-error" : undefined,
                  }}
                  FormHelperTextProps={{ id: "create-user-uuid-error" }}
                  size="small"
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setUuid(generateUUID())}
                  disabled={submittingUser}
                  startIcon={<HiRefresh size={14} />}
                  sx={{ mt: 0.25, minWidth: 130, flexShrink: 0 }}
                >
                  Auto-generar
                </Button>
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                Información Personal
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <TextField
                    label="Nombre"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    error={!!errors.name}
                    helperText={errors.name}
                    disabled={submittingUser}
                    placeholder="Ej: Juan"
                    id="create-user-name"
                    size="small"
                    sx={{ flex: "1 1 200px" }}
                  />
                  <TextField
                    label="Apellido"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={submittingUser}
                    placeholder="Ej: Pérez"
                    size="small"
                    sx={{ flex: "1 1 200px" }}
                  />
                </Box>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <TextField
                    label="Segundo Nombre"
                    value={middleName}
                    onChange={(e) => setMiddleName(e.target.value)}
                    disabled={submittingUser}
                    size="small"
                    sx={{ flex: "1 1 200px" }}
                  />
                  <TextField
                    label="Apodo"
                    value={nickName}
                    onChange={(e) => setNickName(e.target.value)}
                    disabled={submittingUser}
                    size="small"
                    sx={{ flex: "1 1 200px" }}
                  />
                </Box>
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                Documento de Identidad
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Autocomplete
                  options={idTypes}
                  getOptionLabel={(option) => option.name || ""}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={idType}
                  onChange={(_, value) => setIdType(value)}
                  disabled={submittingUser}
                  sx={{ flex: "1 1 200px" }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tipo de Documento"
                      required
                      error={!!errors.idType}
                      helperText={errors.idType}
                      id="create-user-id-type"
                      size="small"
                    />
                  )}
                />
                <TextField
                  label="Número de Documento"
                  required
                  value={idNumber}
                  onChange={(e) => setIdNumber(e.target.value)}
                  error={!!errors.idNumber}
                  helperText={errors.idNumber}
                  disabled={submittingUser}
                  placeholder="Ej: 12345678"
                  size="small"
                  sx={{ flex: "1 1 200px" }}
                />
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                Contacto y Ubicación
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <TextField
                    label="Correo Electrónico"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={submittingUser}
                    placeholder="Ej: juan@ejemplo.com"
                    size="small"
                    sx={{ flex: "1 1 200px" }}
                  />
                  <TextField
                    label="Fecha de Nacimiento"
                    type="date"
                    value={birthdate}
                    onChange={(e) => setBirthdate(e.target.value)}
                    disabled={submittingUser}
                    size="small"
                    sx={{ flex: "1 1 200px" }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <TextField
                    label="Dirección"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    disabled={submittingUser}
                    placeholder="Ej: Calle 10 #5-20"
                    size="small"
                    sx={{ flex: "1 1 200px" }}
                  />
                  <Autocomplete
                    options={[
                      { value: "M", label: "Masculino" },
                      { value: "F", label: "Femenino" },
                      { value: "O", label: "Otro" },
                    ]}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    value={
                      gender
                        ? {
                            value: gender,
                            label:
                              gender === "M" ? "Masculino" : gender === "F" ? "Femenino" : "Otro",
                          }
                        : null
                    }
                    onChange={(_, value) => setGender(value?.value || "")}
                    disabled={submittingUser}
                    sx={{ flex: "1 1 200px" }}
                    renderInput={(params) => <TextField {...params} label="Género" size="small" />}
                  />
                </Box>
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                Estado
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    disabled={submittingUser}
                    color="success"
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip
                      label={isActive ? "Activo" : "Inactivo"}
                      color={isActive ? "success" : "default"}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                }
              />
            </Box>
          </Box>
        )}

        {activeStep === 1 && (
          <Box>
            {createdUserId && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Usuario creado con ID: <strong>{createdUserId}</strong>. Ahora puedes asignarlo a
                una organización o omitir este paso.
              </Alert>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={skipAssignment}
                  onChange={(e) => {
                    setSkipAssignment(e.target.checked);
                    if (e.target.checked) {
                      setSelectedOrg(null);
                      setSelectedRole(null);
                    }
                  }}
                  color="warning"
                />
              }
              label="Omitir asignación de organización"
              sx={{ mb: 2 }}
            />

            {!skipAssignment && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Selecciona la organización y el rol para el usuario.
                </Typography>
                <Autocomplete
                  options={organizations}
                  getOptionLabel={(option) => option.name || ""}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={selectedOrg}
                  onChange={(_, value) => {
                    setSelectedOrg(value);
                    setSelectedRole(null);
                  }}
                  disabled={submittingAssignment}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Organización"
                      placeholder="Buscar organización..."
                      id="create-user-org-select"
                      size="small"
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <HiSearch size={16} style={{ opacity: 0.5 }} />
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  )}
                />
                {selectedOrg && (
                  <>
                    {rolesLoading ? (
                      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : roles.length === 0 ? (
                      <Alert severity="info">Esta organización no tiene roles configurados.</Alert>
                    ) : (
                      <Autocomplete
                        options={roles}
                        getOptionLabel={(option) => option.name || ""}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={selectedRole}
                        onChange={(_, value) => setSelectedRole(value)}
                        disabled={submittingAssignment}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Rol"
                            placeholder="Buscar rol..."
                            id="create-user-role-select"
                            size="small"
                          />
                        )}
                      />
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={submittingUser || submittingAssignment}
          sx={{ minWidth: 100 }}
        >
          {activeStep === 1 ? "Cancelar" : "Cancelar"}
        </Button>
        {activeStep === 0 && (
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={submittingUser}
            startIcon={
              submittingUser ? <CircularProgress size={16} color="inherit" /> : <HiPlus size={16} />
            }
            sx={{ minWidth: 140 }}
          >
            {submittingUser ? "Creando..." : "Crear Usuario"}
          </Button>
        )}
        {activeStep === 1 && (
          <>
            <Button
              onClick={() => {
                onSuccess();
                onClose();
              }}
              disabled={submittingAssignment}
            >
              Omitir
            </Button>
            <Button
              variant="contained"
              onClick={handleAssignAndFinish}
              disabled={
                (!skipAssignment && (!selectedOrg || !selectedRole)) || submittingAssignment
              }
              startIcon={
                submittingAssignment ? <CircularProgress size={16} color="inherit" /> : undefined
              }
              sx={{ minWidth: 160 }}
            >
              {submittingAssignment
                ? "Asignando..."
                : skipAssignment
                  ? "Finalizar"
                  : "Asignar y Finalizar"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateUserModal;
